import {apolloPlatformAxiosInstance} from '@/api/apolloPlatform/apolloPlatformAxiosInstance';

/**
 * Generic type that represents paginated responses from Django rest_framework.
 * @see {@link apollo_platform/config/api/pagination.py}
 */
export type LimitOffsetPagination<Type> = {
  next: string | null;
  previous: string | null;
  limit: number;
  offset: number;
  results: Type; // N.B. admin responses return config objects.
  total: number;
};

export type LimitOffsetPaginationParams = {
  next?: string;
  previous?: string;
  limit?: number;
  offset?: number;
};

export const cursor = <Type>(arg: Type): Type => arg;

const aggregatePaginationResults = async <StoreType>(
  apiModule: (...args: any) => Promise<AxiosResponse<LimitOffsetPagination<StoreType[]>>>,
  startingNext: string,
): Promise<StoreType[]> => {
  try {
    // Add store types as needed
    const aggregatedPaginationResults: StoreType[] = [];
    // Set the initial cursor url
    let next: string | null = startingNext?.length > 0 ? startingNext : null;

    do {
      // Get the next cursor url param
      let data: LimitOffsetPagination<StoreType>;

      /*
       * This is used by /customers & /invitations
       * TODO: migrate /invitations to use CustomLimitOffsetPagination
       */

      // Get the next cursor data
      if (next) {
        const response: AxiosResponse = await apolloPlatformAxiosInstance.get(next);
        data = cursor(response.data);
      } else {
        const response: AxiosResponse = await apiModule();
        data = cursor(response.data);
      }

      // Aggregate the data
      if (Array.isArray(data.results) === false)
        throw 'Response results must be iterable to aggregate.';
      aggregatedPaginationResults.push(...data.results);

      /*
       * `/invitations` & `/customers` use the default `CursorPagination` class
       * When last cursor, the next cursor may be same value, causing a loop
       */
      const isLast = next === data.next;

      if (data.next && isLast === false) {
        next = data.next;
      } else {
        next = null;
      }
    } while (next);

    // Return the aggregated data
    return aggregatedPaginationResults;
  } catch (error) {
    throw apiErrorMessage(error);
  }
};

/**
 * Generic Function provided with a StoreType
 * @example <caption>Example using the Customer type.</caption>
 * // Returns Customer[] using the apiGetCustomers module
 * const data: Customer[] = await apiGetPaginatedResults(apiGetCustomers);
 * @param {apiModuleCallback} apiModule An API module from the api folder that supports the cursor param.
 * @returns {Promise<StoreType[]>} An aggregated data response of StoreType type parameter.
 */
export const apiGetPaginatedResults = async <StoreType>(
  apiModule: (...args: any) => Promise<AxiosResponse<LimitOffsetPagination<StoreType[]>>>,
  startingNext: string,
): Promise<StoreType[]> => {
  return await combineRequest(`cached:${apiModule.name}`, () =>
    aggregatePaginationResults<StoreType>(apiModule, startingNext),
  );
};
